import classNames from 'classnames';
import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react';
import Box, { BoxProps } from 'ui-box';

import AutoSizer from 'react-virtualized-auto-sizer';
import { Button } from '../Button';
import { FixedSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';

export interface ITableProps extends BoxProps<'table'> {
  dynamic?: boolean;
  large?: boolean;
  isLoading?: boolean;
  onScrollNearBottom?: () => void;
  prePad?: boolean;
}

export const Table: React.FC<ITableProps> = ({
  dynamic,
  large = false,
  isLoading = false,
  prePad = false,
  children,
  onScrollNearBottom,
  ...props
}) => {
  const onScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement, UIEvent>) => {
      const target = event.target as HTMLDivElement;
      const scroll = target.scrollHeight - target.scrollTop;
      // If we get below the remaining target height, we should fire onScrollNearBottom.
      if (scroll - target.clientHeight < target.clientHeight * 2) {
        onScrollNearBottom && onScrollNearBottom();
      }
    },
    [onScrollNearBottom]
  );

  return (
    <Box
      {...props}
      onScroll={onScroll}
      className={classNames(
        'table-container',
        dynamic && 'dynamic',
        large && 'large',
        isLoading && 'loading',
        prePad && 'prepad',
        props.className
      )}
    >
      <table>
        {children}
        <tfoot>
          {isLoading &&
            new Array(12).fill(0).map((_, i) => (
              <tr key={i} className="loading">
                <td colSpan={100}></td>
              </tr>
            ))}
        </tfoot>
      </table>
    </Box>
  );
};

interface IndicesSelection {
  [key: number]: boolean;
}

export interface VirtualTableHeaderItem {
  title: string;
  orderByKeys?: string[];
  align?: 'left' | 'right' | 'center';
  width?: number;
  footer?: React.ReactNode;
}

export interface IVirtualTableProps<T extends { id: any }> {
  isLoading?: boolean;
  items: T[];
  onRequestMore?: () => Promise<any>;
  renderRow: (props: { item: T; index: number }) => React.ReactElement;
  rowHeight?: number;
  total: number;
  ordering?: string;
  onOrderingChange?: (ordering: string) => void;
  noItemsPlaceholder?: React.ReactNode;
  header: VirtualTableHeaderItem[];
  footer?: boolean;
  onSelect?: (indices: IndicesSelection) => void;
  selection?: IndicesSelection;
}

export function VirtualTable<T extends { id: any }>({
  onRequestMore,
  ordering,
  onOrderingChange,
  noItemsPlaceholder,
  onSelect,
  selection,
  rowHeight = 48,
  ...props
}: IVirtualTableProps<T>) {
  const [tableRef, setTableRef] = useState<HTMLDivElement | null>(null);

  const HEADER_HEIGHT = 34;
  const BUFFER_CELLS = 2;

  const [visibleIndices, setVisibleIndices] = useState({
    start: 0,
    end: BUFFER_CELLS,
  });

  const onScroll = useCallback(
    (target: HTMLDivElement) => {
      const missingPercentage = props.items.length / props.total;

      const scroll = target.scrollTop + target.clientHeight + 200;

      const lastVisibleIndex = Math.ceil(
        (scroll / target.scrollHeight) * props.total
      );

      const firstVisibleIndex = Math.floor(
        (target.scrollTop / (target.scrollHeight - 200)) * props.total
      );

      if (
        lastVisibleIndex > visibleIndices.end ||
        firstVisibleIndex < visibleIndices.start
      ) {
        setVisibleIndices({
          start: firstVisibleIndex - BUFFER_CELLS,
          end: lastVisibleIndex + BUFFER_CELLS,
        });
      }

      // If we get below the remaining target height, we should fire onScrollNearBottom.
      if (
        target.scrollTop >
        target.scrollHeight * missingPercentage - target.scrollHeight
      ) {
        !props.isLoading && onRequestMore && onRequestMore();
      }
    },
    [
      onRequestMore,
      props.isLoading,
      props.items.length,
      props.total,
      visibleIndices.end,
      visibleIndices.start,
    ]
  );

  useEffect(() => {
    if (tableRef) {
      onScroll(tableRef);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableRef]);

  const gridColumns = props.header
    .map((h) => (h.width ? `${h.width}px` : `minmax(240px, 1fr)`))
    .join(' ');

  // Computation for ordering in headers
  const orderByKeys = ordering
    ? ordering.split(',').map((k) => k.split('-').pop())
    : null;
  const orderDirection = ordering
    ? ordering.indexOf('-') > -1
      ? '-'
      : ''
    : null;
  return (
    <Box height="100%" className="dynamic">
      <AutoSizer>
        {({ width, height }) => (
          <Box
            ref={(ref: HTMLDivElement) => {
              setTableRef(ref);
            }}
            className="virtual-table"
            width={width}
            height={height}
            overflow="auto"
            position="relative"
            onScroll={(e: any) => onScroll(e.target)}
          >
            <InfiniteLoader
              isItemLoaded={(index) => props.items.length > index}
              itemCount={props.total + 1}
              loadMoreItems={async (s, e) => {
                onRequestMore && (await onRequestMore());
              }}
              minimumBatchSize={50}
              threshold={15}
            >
              {({ onItemsRendered, ref }) => (
                <FixedSizeList
                  height={height}
                  width="100%"
                  itemSize={rowHeight}
                  itemCount={props.total}
                  itemData={props.items}
                  onItemsRendered={onItemsRendered}
                  ref={ref}
                  innerElementType={forwardRef(
                    ({ style, children, ...innerProps }, ref) => (
                      <Box
                        ref={ref}
                        style={{
                          ...style,
                          position: 'relative',
                          minHeight: '100%',
                          height: `${
                            parseFloat(style.height) + HEADER_HEIGHT + 200
                          }px`,
                        }}
                        {...innerProps}
                      >
                        <Box
                          display="grid"
                          gridAutoFlow="column"
                          gridTemplateColumns={gridColumns}
                          alignItems="center"
                          height={HEADER_HEIGHT}
                          position="sticky"
                          top={0}
                          zIndex={3}
                          transform="translate3d(0,0,0)"
                        >
                          {props.header.map((h, i) => (
                            <Box
                              key={i}
                              className="table-th"
                              backgroundColor="white"
                              textAlign={h.align || 'left'}
                            >
                              <span className="overflow-ellipsis">
                                {h.title}
                              </span>
                              {h.orderByKeys && onOrderingChange && (
                                <Button
                                  flexGrow={0}
                                  small
                                  minimal
                                  marginY={-4}
                                  intent={
                                    orderByKeys &&
                                    h.orderByKeys.join(',') ===
                                      orderByKeys.join(',')
                                      ? 'warning'
                                      : 'none'
                                  }
                                  iconLeft={
                                    orderByKeys &&
                                    h.orderByKeys.join(',') ===
                                      orderByKeys.join(',')
                                      ? orderDirection === '-'
                                        ? 'sort-desc'
                                        : 'sort-asc'
                                      : 'sort'
                                  }
                                  onClick={() => {
                                    // Compute if we change ordering, and if so do not change direction
                                    const direction =
                                      orderByKeys &&
                                      h.orderByKeys!.join(',') ===
                                        orderByKeys.join(',')
                                        ? orderDirection === '-'
                                          ? ''
                                          : '-'
                                        : orderDirection;
                                    onOrderingChange(
                                      h
                                        .orderByKeys!.map(
                                          (k) => `${direction}${k}`
                                        )
                                        .join(',')
                                    );
                                  }}
                                />
                              )}
                            </Box>
                          ))}
                        </Box>
                        {children}
                        {props.footer && (
                          <Box
                            display="grid"
                            gridAutoFlow="column"
                            gridTemplateColumns={gridColumns}
                            alignItems="center"
                            position="sticky"
                            bottom={0}
                            top={height - HEADER_HEIGHT}
                            left={0}
                            zIndex={3}
                          >
                            {props.header.map((header, i) => (
                              <Box
                                key={i}
                                height={HEADER_HEIGHT}
                                className="table-th"
                                backgroundColor="white"
                              >
                                {header.footer && (
                                  <Box width="100%" textAlign="right">
                                    <span
                                      className="muted"
                                      style={{ fontSize: '10px' }}
                                    >
                                      Sum:
                                    </span>{' '}
                                    <span
                                      className="font-mono medium"
                                      style={{
                                        fontSize: '12px',
                                        textTransform: 'none',
                                      }}
                                    >
                                      {header.footer}
                                    </span>
                                  </Box>
                                )}
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    )
                  )}
                >
                  {memo(({ style, ...rowProps }) => {
                    const loaded = props.items.length > rowProps.index;
                    return (
                      <Box
                        style={{
                          ...style,
                          top: `${
                            parseFloat(style.top as string) + HEADER_HEIGHT
                          }px`,
                        }}
                        display="grid"
                        gridAutoFlow="column"
                        alignItems="center"
                        gridTemplateColumns={loaded ? gridColumns : '1fr'}
                        className={classNames(
                          'row',
                          selection && selection[rowProps.index] && 'selected'
                        )}
                        onClick={() => {
                          if (!onSelect) {
                            return;
                          }
                          const s = selection || {};
                          onSelect({
                            ...s,
                            [rowProps.index]: !s[rowProps.index],
                          });
                        }}
                      >
                        {loaded ? (
                          <props.renderRow
                            item={props.items[rowProps.index]}
                            index={rowProps.index}
                          />
                        ) : (
                          <Box
                            className="loading skeleton"
                            borderRadius={4}
                            height={rowHeight - 8}
                            marginTop={8}
                          />
                        )}
                      </Box>
                    );
                  })}
                </FixedSizeList>
              )}
            </InfiniteLoader>
          </Box>
        )}
      </AutoSizer>
      {props.total === 0 && (
        <Box position="absolute" top="64px" width="100%">
          {noItemsPlaceholder}
        </Box>
      )}
    </Box>
  );
}

export const VirtualTableItem: React.FC<BoxProps<any>> = (props) => {
  return (
    <Box
      height="100%"
      display="flex"
      alignItems="center"
      justifyContent="flex-start"
      overflowX="auto"
      {...props}
      title={
        typeof props.children === 'string' || typeof props.children === 'number'
          ? props.children
          : props.title
      }
      className={classNames('item elipsis hide-scroll-bar', props.className)}
    />
  );
};
